import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTour } from "../context/TourContext";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

interface TargetRect { top: number; left: number; width: number; height: number; }

export default function AppTour() {
  const { isActive, currentStep, steps, lang, setLang, nextStep, prevStep, skipTour, endTour } = useTour();
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const scrollTargetIntoView = useCallback((el: Element) => {
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight;
    if (rect.top < 100 || rect.bottom > vh - 100) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  const updateTargetPosition = useCallback(() => {
    const step = steps[currentStep];
    if (!step) return;
    const elements = document.querySelectorAll(step.target);
    let visibleEl: Element | null = null;
    for (const el of elements) {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0 && rect.left >= 0 && rect.left <= window.innerWidth) {
        visibleEl = el;
        break;
      }
    }
    if (visibleEl) {
      const rect = visibleEl.getBoundingClientRect();
      setTargetRect({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
    } else {
      setTargetRect(null);
    }
  }, [currentStep, steps]);

  useEffect(() => {
    if (!isActive) return;
    setIsAnimating(true);
    const animTimer = setTimeout(() => setIsAnimating(false), 250);

    const step = steps[currentStep];
    if (step) {
      const elements = document.querySelectorAll(step.target);
      for (const el of elements) {
        const rect = el.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) { scrollTargetIntoView(el); break; }
      }
    }

    const measureTimer = setTimeout(updateTargetPosition, 300);
    window.addEventListener("resize", updateTargetPosition);
    window.addEventListener("scroll", updateTargetPosition, true);
    return () => {
      clearTimeout(animTimer);
      clearTimeout(measureTimer);
      window.removeEventListener("resize", updateTargetPosition);
      window.removeEventListener("scroll", updateTargetPosition, true);
    };
  }, [isActive, currentStep, updateTargetPosition, scrollTargetIntoView, steps]);

  if (!isActive || !steps[currentStep]) return null;

  const step = steps[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === steps.length - 1;

  const getTooltipStyle = (): React.CSSProperties => {
    if (!targetRect) return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const gap = 12;
    const m = 16;
    const w = Math.min(300, vw - m * 2);
    const isBottom = targetRect.top > vh * 0.55;
    const isSidebarEl = targetRect.left < 260 && targetRect.width < 200;

    if (vw < 768) {
      const v = isBottom ? { bottom: vh - targetRect.top + gap } : { top: targetRect.top + targetRect.height + gap };
      return { ...v, left: m, right: m, maxWidth: vw - m * 2 };
    }

    if (isSidebarEl) {
      return {
        top: Math.max(m, Math.min(targetRect.top, vh - 220)),
        left: Math.min(targetRect.left + targetRect.width + gap, vw - m - w),
        width: w,
      };
    }

    const cx = targetRect.left + targetRect.width / 2;
    const left = Math.max(m, Math.min(cx - w / 2, vw - m - w));
    const v = isBottom ? { bottom: vh - targetRect.top + gap } : { top: targetRect.top + targetRect.height + gap };
    return { ...v, left, width: w };
  };

  return createPortal(
    <div className="fixed inset-0 z-[99999]">
      {/* Backdrop with cutout */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none">
        <defs>
          <mask id="tour-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect
                x={targetRect.left - 5} y={targetRect.top - 5}
                width={targetRect.width + 10} height={targetRect.height + 10}
                rx="10" fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0" y="0" width="100%" height="100%"
          fill="rgba(0, 0, 0, 0.65)"
          mask="url(#tour-mask)"
          className="pointer-events-auto"
          onClick={skipTour}
        />
      </svg>

      {/* Highlight ring */}
      {targetRect && (
        <div
          className="absolute pointer-events-none transition-all duration-300 ease-out"
          style={{
            top: targetRect.top - 5, left: targetRect.left - 5,
            width: targetRect.width + 10, height: targetRect.height + 10,
            borderRadius: 10,
            boxShadow: "0 0 0 2px rgba(139, 92, 246, 0.7), 0 0 16px rgba(139, 92, 246, 0.25)",
          }}
        />
      )}

      {/* Tooltip */}
      <div
        className={`absolute bg-white dark:bg-dark-800 rounded-xl shadow-2xl border border-gray-200 dark:border-dark-600 overflow-hidden transition-all duration-250 ${isAnimating ? "scale-95 opacity-0" : "scale-100 opacity-100"}`}
        style={getTooltipStyle()}
      >
        {/* Title row */}
        <div className="flex items-center justify-between px-4 pt-3.5 pb-1">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-gray-900 dark:text-white font-semibold text-[15px] truncate">
              {lang === "es" ? step.titleEs : step.title}
            </span>
            <span className="text-[11px] text-gray-400 dark:text-dark-500 tabular-nums flex-shrink-0">
              {currentStep + 1}/{steps.length}
            </span>
          </div>
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <button
              onClick={() => setLang(lang === "en" ? "es" : "en")}
              className="px-1.5 py-0.5 text-[11px] rounded hover:bg-gray-100 dark:hover:bg-dark-700 text-gray-400 dark:text-dark-500 transition-colors"
            >
              {lang === "en" ? "🇪🇸" : "🇺🇸"}
            </button>
            <button onClick={skipTour} className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-dark-700 text-gray-400 dark:text-dark-400 transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-4 pt-0.5 pb-3">
          <p className="text-gray-500 dark:text-dark-300 text-[13px] leading-snug">
            {lang === "es" ? step.contentEs : step.content}
          </p>
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 bg-gray-50 dark:bg-dark-900 border-t border-gray-100 dark:border-dark-700 flex items-center justify-between">
          <div>
            {isFirst ? (
              <button onClick={skipTour} className="text-[12px] text-gray-400 dark:text-dark-500 hover:text-gray-600 dark:hover:text-dark-300 transition-colors">
                {lang === "es" ? "Saltar" : "Skip tour"}
              </button>
            ) : (
              <button onClick={prevStep} className="flex items-center gap-0.5 text-[12px] text-gray-500 dark:text-dark-400 hover:text-gray-700 dark:hover:text-dark-200 transition-colors">
                <ChevronLeft className="w-3.5 h-3.5" />
                {lang === "es" ? "Atrás" : "Back"}
              </button>
            )}
          </div>
          <button
            onClick={isLast ? endTour : nextStep}
            className="flex items-center gap-1 px-3.5 py-1.5 text-[12px] font-semibold bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
          >
            {isLast ? (lang === "es" ? "Listo" : "Done") : (lang === "es" ? "Siguiente" : "Next")}
            {!isLast && <ChevronRight className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
